import React, { useState, useEffect } from 'react';
import HeaderBar from '../components/HeaderBar';
import ConfirmModal from '../components/ConfirmModal';
import { inviteService, Invite, GenerateInviteResponse } from '../services/inviteService';

const InvitesList: React.FC = () => {
  const [invites, setInvites] = useState<Invite[]>([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null);
  const [copiedToken, setCopiedToken] = useState<string | null>(null);
  const [generatedInvite, setGeneratedInvite] = useState<GenerateInviteResponse | null>(null);

  const fetchInvites = async () => {
    try {
      setLoading(true);
      const data = await inviteService.listInvites();
      setInvites(data);
    } catch (error) {
      console.error('Failed to fetch invites:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInvites();
  }, []);

  const handleGenerate = async () => {
    try {
      setGenerating(true);
      const result = await inviteService.generateInvite();
      setGeneratedInvite(result);
      await fetchInvites();
    } catch (error) {
      console.error('Failed to generate invite:', error);
    } finally {
      setGenerating(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await inviteService.deleteInvite(deleteTarget);
      setDeleteTarget(null);
      await fetchInvites();
    } catch (error) {
      console.error('Failed to delete invite:', error);
    }
  };

  const copyToClipboard = (text: string, token: string) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedToken(token);
      setTimeout(() => setCopiedToken(null), 2000);
    });
  };

  const getStatusBadge = (invite: Invite) => {
    if (invite.isUsed) {
      return { label: 'Usado', bg: '#e0f2f1', color: '#00796b' };
    }
    if (invite.isExpired) {
      return { label: 'Expirado', bg: '#fef2f2', color: '#991b1b' };
    }
    return { label: 'Pendiente', bg: '#fffbeb', color: '#92400e' };
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const truncateToken = (token: string) => {
    return token.length > 12 ? `${token.slice(0, 8)}...${token.slice(-4)}` : token;
  };

  const buildInviteUrl = (token: string) => {
    const baseUrl = process.env.REACT_APP_INVITE_BASE_URL || 'http://localhost:8001';
    return `${baseUrl}/backdoor?token=${token}`;
  };

  return (
    <div style={{ padding: 0 }}>
      <HeaderBar title="Invitaciones" />
      <div style={{ maxWidth: 1200, margin: '0 auto', padding: '0 24px' }}>
        {/* Generate button row */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', marginBottom: 24 }}>
          <button
            onClick={handleGenerate}
            disabled={generating}
            style={{
              background: generating ? '#a0d8d4' : '#38b2ac',
              color: '#fff',
              border: 'none',
              borderRadius: 8,
              padding: '10px 20px',
              fontSize: 16,
              fontWeight: 600,
              cursor: generating ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              boxShadow: '0 2px 4px rgba(56, 178, 172, 0.2)',
              transition: 'all 0.2s',
            }}
            onMouseEnter={(e) => {
              if (!generating) {
                e.currentTarget.style.background = '#319795';
                e.currentTarget.style.transform = 'translateY(-1px)';
                e.currentTarget.style.boxShadow = '0 4px 8px rgba(56, 178, 172, 0.3)';
              }
            }}
            onMouseLeave={(e) => {
              if (!generating) {
                e.currentTarget.style.background = '#38b2ac';
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 2px 4px rgba(56, 178, 172, 0.2)';
              }
            }}
          >
            <span style={{ fontSize: 20, lineHeight: 1 }}>+</span>
            <span>{generating ? 'Generando...' : 'Generar Invitación'}</span>
          </button>
        </div>

        {/* Generated invite banner */}
        {generatedInvite && (
          <div
            style={{
              background: '#f0fdfa',
              border: '1px solid #99f6e4',
              borderRadius: 10,
              padding: '16px 20px',
              marginBottom: 24,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 16,
            }}
          >
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 600, fontSize: 14, color: '#0f766e', marginBottom: 4 }}>
                Invitación generada
              </div>
              <div
                style={{
                  fontSize: 13,
                  color: '#115e59',
                  wordBreak: 'break-all',
                  fontFamily: 'monospace',
                }}
              >
                {generatedInvite.url}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
              <button
                onClick={() => copyToClipboard(generatedInvite.url, 'generated')}
                style={{
                  background: '#38b2ac',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 6,
                  padding: '8px 14px',
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: 'pointer',
                  transition: 'background 0.2s',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = '#319795'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = '#38b2ac'; }}
              >
                {copiedToken === 'generated' ? 'Copiado!' : 'Copiar URL'}
              </button>
              <button
                onClick={() => setGeneratedInvite(null)}
                style={{
                  background: 'transparent',
                  color: '#6b7280',
                  border: '1px solid #d1d5db',
                  borderRadius: 6,
                  padding: '8px 12px',
                  fontSize: 13,
                  fontWeight: 500,
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = '#f9fafb'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
              >
                ✕
              </button>
            </div>
          </div>
        )}

        {/* Table */}
        {loading ? (
          <div style={{ padding: 40, fontSize: 18, color: '#666', textAlign: 'center' }}>
            Cargando invitaciones...
          </div>
        ) : invites.length === 0 ? (
          <div style={{ padding: 40, fontSize: 18, color: '#666', textAlign: 'center' }}>
            No hay invitaciones
          </div>
        ) : (
          <div style={{ background: '#fff', borderRadius: 12, boxShadow: '0 1px 3px rgba(0,0,0,0.1)', overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                  <th style={{ padding: '16px 20px', textAlign: 'left', fontWeight: 600, color: '#374151', fontSize: 14 }}>
                    TOKEN
                  </th>
                  <th style={{ padding: '16px 20px', textAlign: 'left', fontWeight: 600, color: '#374151', fontSize: 14 }}>
                    ESTADO
                  </th>
                  <th style={{ padding: '16px 20px', textAlign: 'left', fontWeight: 600, color: '#374151', fontSize: 14 }}>
                    CREADO POR
                  </th>
                  <th style={{ padding: '16px 20px', textAlign: 'left', fontWeight: 600, color: '#374151', fontSize: 14 }}>
                    USADO POR
                  </th>
                  <th style={{ padding: '16px 20px', textAlign: 'left', fontWeight: 600, color: '#374151', fontSize: 14 }}>
                    CREADO
                  </th>
                  <th style={{ padding: '16px 20px', textAlign: 'left', fontWeight: 600, color: '#374151', fontSize: 14 }}>
                    EXPIRA
                  </th>
                  <th style={{ padding: '16px 20px', textAlign: 'center', fontWeight: 600, color: '#374151', fontSize: 14 }}>
                    ACCIONES
                  </th>
                </tr>
              </thead>
              <tbody>
                {invites.map((invite, index) => {
                  const status = getStatusBadge(invite);
                  return (
                    <tr
                      key={invite.token}
                      style={{
                        borderBottom: index < invites.length - 1 ? '1px solid #f1f5f9' : 'none',
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.background = '#f8fafc'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                    >
                      <td style={{ padding: '16px 20px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <code style={{
                            fontSize: 13,
                            background: '#f1f5f9',
                            padding: '4px 8px',
                            borderRadius: 4,
                            color: '#334155',
                          }}>
                            {truncateToken(invite.token)}
                          </code>
                          <button
                            onClick={() => copyToClipboard(buildInviteUrl(invite.token), invite.token)}
                            title="Copiar URL completa"
                            style={{
                              background: 'transparent',
                              border: 'none',
                              cursor: 'pointer',
                              padding: 4,
                              fontSize: 14,
                              color: copiedToken === invite.token ? '#38b2ac' : '#9ca3af',
                              transition: 'color 0.2s',
                            }}
                            onMouseEnter={(e) => { e.currentTarget.style.color = '#38b2ac'; }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.color = copiedToken === invite.token ? '#38b2ac' : '#9ca3af';
                            }}
                          >
                            {copiedToken === invite.token ? '✓' : '⧉'}
                          </button>
                        </div>
                      </td>
                      <td style={{ padding: '16px 20px' }}>
                        <span style={{
                          display: 'inline-block',
                          background: status.bg,
                          color: status.color,
                          padding: '4px 10px',
                          borderRadius: 12,
                          fontSize: 12,
                          fontWeight: 600,
                        }}>
                          {status.label}
                        </span>
                      </td>
                      <td style={{ padding: '16px 20px', color: '#6b7280', fontSize: 14 }}>
                        {invite.createdBy.email}
                      </td>
                      <td style={{ padding: '16px 20px', color: '#6b7280', fontSize: 14 }}>
                        {invite.usedBy ? invite.usedBy.email : '—'}
                      </td>
                      <td style={{ padding: '16px 20px', color: '#6b7280', fontSize: 14 }}>
                        {formatDate(invite.createdAt)}
                      </td>
                      <td style={{ padding: '16px 20px', color: '#6b7280', fontSize: 14 }}>
                        {formatDate(invite.expiresAt)}
                      </td>
                      <td style={{ padding: '16px 20px', textAlign: 'center' }}>
                        <button
                          onClick={() => setDeleteTarget(invite.token)}
                          title="Eliminar invitación"
                          style={{
                            background: 'transparent',
                            border: '1px solid #e5e7eb',
                            borderRadius: 6,
                            padding: '6px 12px',
                            fontSize: 13,
                            fontWeight: 500,
                            color: '#ef4444',
                            cursor: 'pointer',
                            transition: 'all 0.2s',
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = '#fef2f2';
                            e.currentTarget.style.borderColor = '#fecaca';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = 'transparent';
                            e.currentTarget.style.borderColor = '#e5e7eb';
                          }}
                        >
                          Eliminar
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <ConfirmModal
        open={deleteTarget !== null}
        title="Eliminar invitación"
        message={
          <>
            ¿Estás seguro de que deseas eliminar la invitación{' '}
            <strong>{deleteTarget ? truncateToken(deleteTarget) : ''}</strong>?
            Esta acción no se puede deshacer.
          </>
        }
        confirmText="Eliminar"
        cancelText="Cancelar"
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
};

export default InvitesList;
